import React from 'react';
import { logError } from '@/utilities/logger';
import { formatMessage } from '@/utilities/logger.utilities';
import { incrementBrowserErrorCount } from '@seeqdev/telemetry';

interface ErrorBoundaryProps {
  fallback: (error: Error, reset: () => void) => React.ReactNode;
  onError: (error: Error, errorInfo: React.ErrorInfo) => void;
  children?: React.ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch = (error: Error, errorInfo: React.ErrorInfo): void => {
    this.props.onError(error, errorInfo);
  };

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    return this.state.error ? (
      <div className="flexColumnContainer height-maximum width-maximum tw:items-center tw:justify-center">
        {this.props.fallback(this.state.error, this.reset)}
      </div>
    ) : (
      this.props.children
    );
  }
}

/**
 * General purpose mechanism to catch and display React errors
 */
export const ErrorBoundaryWithLogging = ({
  fallback,
  onError,
  children,
}: {
  fallback: ErrorBoundaryProps['fallback'];
  onError?: ErrorBoundaryProps['onError'];
  children?: React.ReactNode;
}) => {
  const persistError = (ex: Error, { componentStack }: React.ErrorInfo) => {
    logError(formatMessage`Unhandled React Error: ${ex}\nat${componentStack}`);
    // React-boundary errors don't reach the global window error handler, so feed
    // the error-rate metric here. logError already emits the detail log record.
    incrementBrowserErrorCount(ex);
  };
  return (
    <ErrorBoundary
      fallback={fallback}
      onError={(error, reactError) => {
        persistError(error, reactError);
        onError?.(error, reactError);
      }}>
      {children}
    </ErrorBoundary>
  );
};
